/**
 * ProjectBacklogTab Component
 * AIDLC Administrative Backend
 *
 * Filtered backlog view for a specific project.
 * Shows backlog items with sprint assignment column and bulk selection
 * to assign multiple items to a sprint at once.
 */

import { useState, useEffect, useCallback } from 'react';
import { Badge } from '../../shared/Badge';
import { Button } from '../../shared/Button';
import { LoadingSpinner } from '../../shared/LoadingSpinner';
import { EmptyState } from '../../shared/EmptyState';
import { getBacklogItems } from '../../../services/backlogService';
import { addItemsToSprint, removeItemFromSprint } from '../../../services/sprintService';
import { useSprints } from '../../../hooks/useSprints';
import type { BacklogItem } from '../../../types/backlog';

interface ProjectBacklogTabProps {
  projectId: string;
}

const priorityVariants: Record<string, 'danger' | 'warning' | 'info' | 'neutral'> = {
  critical: 'danger',
  high: 'warning',
  medium: 'info',
  low: 'neutral',
};

const statusLabels: Record<string, string> = {
  new: 'New',
  open: 'Open',
  in_progress: 'In Progress',
  review: 'Review',
  done: 'Done',
  closed: 'Closed',
};

export function ProjectBacklogTab({ projectId }: ProjectBacklogTabProps) {
  const { sprints, activeSprint } = useSprints(projectId);
  const [items, setItems] = useState<BacklogItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [assignSprintId, setAssignSprintId] = useState<string>('');
  const [isAssigning, setIsAssigning] = useState(false);

  const loadItems = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getBacklogItems({ project: projectId });
      setItems(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load backlog items');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Default assign target to active sprint
  useEffect(() => {
    if (activeSprint && !assignSprintId) {
      setAssignSprintId(activeSprint.id);
    }
  }, [activeSprint, assignSprintId]);

  const toggleSelect = (itemId: string) => {
    setSelectedIds((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === items.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(items.map((i) => i.id));
    }
  };

  const handleBulkAssign = async () => {
    if (!assignSprintId || selectedIds.length === 0) return;
    setIsAssigning(true);
    try {
      await addItemsToSprint(assignSprintId, selectedIds);
      setSelectedIds([]);
      await loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign items to sprint');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemoveFromSprint = async (itemId: string, sprintId: string) => {
    try {
      await removeItemFromSprint(sprintId, itemId);
      await loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove item from sprint');
    }
  };

  const getSprintName = (sprintId: string | null | undefined): string => {
    if (!sprintId) return 'Unassigned';
    const sprint = sprints.find((s) => s.id === sprintId);
    return sprint?.name || sprintId;
  };

  const formatDate = (ts: string) => {
    return new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (isLoading) return <LoadingSpinner message="Loading project backlog..." />;

  return (
    <div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-700 text-xs">{error}</p>
          <button onClick={() => setError(null)} className="text-red-600 text-xs underline mt-1">Dismiss</button>
        </div>
      )}

      {/* Bulk assignment toolbar */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-xs font-medium text-ldaja-blue">
            {selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} selected
          </span>
          <select
            value={assignSprintId}
            onChange={(e) => setAssignSprintId(e.target.value)}
            className="text-xs border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-ldaja-blue/50"
          >
            <option value="">Select sprint...</option>
            {sprints.filter((s) => s.status !== 'completed' && s.status !== 'cancelled').map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} {s.status === 'active' ? '(Active)' : ''}
              </option>
            ))}
          </select>
          <Button
            variant="primary"
            size="sm"
            onClick={handleBulkAssign}
            isLoading={isAssigning}
            disabled={!assignSprintId}
          >
            Assign to Sprint
          </Button>
          <button
            onClick={() => setSelectedIds([])}
            className="text-xs text-gray-500 hover:text-gray-700 ml-auto"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Backlog table */}
      {items.length === 0 ? (
        <EmptyState
          title="No backlog items"
          description="This project has no backlog items yet."
        />
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden dark:bg-zinc-900 dark:border-zinc-800">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === items.length && items.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-ldaja-blue focus:ring-ldaja-blue/50"
                  />
                </th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Title</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Priority</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Sprint</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Assigned</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {items.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(item.id)}
                      onChange={() => toggleSelect(item.id)}
                      className="rounded border-gray-300 text-ldaja-blue focus:ring-ldaja-blue/50"
                    />
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="text-xs font-medium text-gray-800 dark:text-gray-200 max-w-[300px] truncate">
                      {item.title}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge variant={priorityVariants[item.priority] || 'neutral'}>
                      {item.priority}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="text-xs text-gray-600">{statusLabels[item.status] || item.status}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1">
                      <span className={`text-xs ${item.sprintId ? 'text-ldaja-blue font-medium' : 'text-gray-400 italic'}`}>
                        {getSprintName(item.sprintId)}
                      </span>
                      {item.sprintId && (
                        <button
                          onClick={() => handleRemoveFromSprint(item.id, item.sprintId!)}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                          title="Remove from sprint"
                        >
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-600">
                    {item.assignedAgent
                      ? item.assignedAgent.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                      : 'Unassigned'}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">
                    {formatDate(item.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
